import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listOutletsApiOutletsGet, type OutletOut } from '@/lib/api/generated';
import { useOutlet } from '@/contexts/OutletContext';
import { useAuth } from '@/lib/auth/auth-context';

/**
 * GEN-01-03: the fixed sign-in label — 'Welcome, <name> (<outlet>)' under the
 * menu title (Angela, 22 Oct 2026) — in the
 * place of the old outlet dropdown. It is a label, not a control — it shows the
 * outlet of THIS sign-in ('HQ' only for an HQ sign-in) and nothing on a page
 * changes it. To look at another outlet, a page uses its own Outlet filter;
 * to work as another outlet, staff sign out and in again (GEN-01-08).
 */
export function SignedInLabel() {
  const { t } = useTranslation('nav');
  const { signedInOutlet } = useOutlet();
  const { user } = useAuth();
  const [outlets, setOutlets] = useState<OutletOut[]>([]);

  useEffect(() => {
    if (!signedInOutlet) return;
    let cancelled = false;
    listOutletsApiOutletsGet()
      .then((data) => {
        if (!cancelled) setOutlets(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [signedInOutlet]);

  const name = signedInOutlet
    ? (outlets.find((o) => o.id === signedInOutlet)?.name ?? '…')
    : t('hq');

  // Angela, 22 Oct 2026: who is signed in and at which outlet, at a glance —
  // 'Welcome, <name> (<outlet>)' in small pink capitals under the menu title.
  const who = user?.name || (user?.email ?? '').split('@')[0];
  return (
    <span
      className="block text-[11px] font-semibold uppercase leading-snug tracking-wide text-[#E65C6A]"
      data-testid="header-signed-in"
      title={`${t('signedIn')}: ${name}`}
    >
      {t('welcome', { name: who, outlet: name })}
    </span>
  );
}
